import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import axios from "axios";
import { BACKEND_URL } from "../utils/constants";

const fmt = (v) =>
  v === null || v === undefined
    ? "—"
    : typeof v === "number"
      ? Math.round(v * 100) / 100
      : String(v);

const clock = (ts) => {
  try {
    return new Date(ts * 1000).toLocaleTimeString();
  } catch {
    return "";
  }
};

function KV({ obj }) {
  return (
    <Stack spacing={0.15} sx={{ mt: 0.5 }}>
      {Object.entries(obj || {}).map(([k, v]) => (
        <Stack key={k} direction="row" justifyContent="space-between"
               sx={{ fontFamily: "monospace", fontSize: 11.5 }}>
          <span style={{ opacity: 0.7 }}>{k}</span>
          <span style={{ fontWeight: 600 }}>{fmt(v)}</span>
        </Stack>
      ))}
    </Stack>
  );
}

export default function AuditPanel({ open, onClose }) {
  const [entries, setEntries] = useState([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await axios.get(`${BACKEND_URL}/audit?n=100`);
      setEntries(r.data?.entries || []);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Log in to view the audit trail." : "Audit unavailable.");
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        Audit — solver computations
        <Chip size="small" label={String(entries.length)} />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load}>
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Every fire-mission solve the backend computed (newest first). Expand a row to see the
          full step-by-step derivation — geometry, MET/VE corrections, every table lookup (with both
          interpolated rows), site, and the final fire elements — so you can verify the math end to end.
        </Typography>
        {err && <Alert severity="info" sx={{ mt: 1 }}>{err}</Alert>}
        {!err && !entries.length && (
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            No solves recorded yet. Start or correct a fire mission to populate the audit.
          </Typography>
        )}
        <Box sx={{ mt: 1 }}>
          {entries.map((e) => (
            <Accordion key={e.seq} disableGutters sx={{ bgcolor: "transparent" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: "100%" }}>
                  <Chip size="small" label={`#${e.seq}`} />
                  <Typography sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                    {e.weapon} {e.charge} {e.projectile}
                  </Typography>
                  <Chip size="small" variant="outlined" label={e.solver} />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                    QE {fmt(e.out?.qe)} · Az {fmt(e.out?.fire_azimuth)} · {clock(e.ts)}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>INPUTS</Typography>
                    <KV obj={{ ptl: e.ptl, angle: e.angle, charge_in: e.charge_in, projectile_in: e.projectile_in }} />
                    <KV obj={e.met} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>FIRE ELEMENTS (RESULT)</Typography>
                    <KV obj={e.out} />
                  </Box>
                </Box>
                {Array.isArray(e.trace) && e.trace.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                      FULL DERIVATION — {e.trace.length} steps (top → bottom)
                    </Typography>
                    <Stack spacing={0.6} sx={{ mt: 0.75 }}>
                      {e.trace.map((s, i) => (
                        <Box key={i} sx={{ borderLeft: "3px solid", borderColor: "primary.main", pl: 1, py: 0.4,
                                           bgcolor: "action.hover", borderRadius: 0.5 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                              <span style={{ opacity: 0.55 }}>{i + 1}.</span> {s.label}
                            </Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                              {fmt(s.result)}{s.unit ? ` ${s.unit}` : ""}
                            </Typography>
                          </Stack>
                          {s.formula && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: "text.secondary", mt: 0.2 }}>
                              {s.formula}
                            </Typography>
                          )}
                          {s.inputs && Object.keys(s.inputs).length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.3 }}>
                              {Object.entries(s.inputs).map(([k, v]) => (
                                <span key={k} style={{ fontFamily: "monospace", fontSize: 10.5, opacity: 0.7 }}>
                                  {k}={fmt(v)}
                                </span>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
